<template>
  <VLayout column pl-3 pr-3 pt-1 class="tab--container" fill-height>
    <ValidationObserver ref="observer">
      <VForm slot-scope="{ invalid }" class="fill-height" @submit.prevent="updateInvoice">
        <VLayout row wrap fill-height>
          <VFlex v-if="partner" xs12>
            <v-card v-if="partner.notes" class="scrollable-content">
              <v-card-text>
                {{ partner.notes }}
              </v-card-text>
            </v-card>
          </VFlex>
          <VFlex pt-2 xs6 class="pr-3 pl-3">
            <CompanyInput
              v-model="partner"
              :rules="{ required: true, client_and_partner: [partner, client] }"
              :loading="loadingPartners"
              :search-input.sync="partnersSearch"
              :success="Boolean(isPartnerFoundInDB)"
              :items="partners"
              :error="!inputFields.vat_id_publisher.validated"
              :label="inputFields.vat_id_publisher.label"
              return-object
              no-data-text
              searchable
              item-text="Naziv"
              item-value="Davcna"
            />
          </VFlex>
          <VFlex pt-2 xs6 class="pr-3 pl-3">
            <CompanyInput
              v-model="client"
              :rules="{ required: true, client_and_partner: [client, partner] }"
              :loading="loadingClients"
              :search-input.sync="clientSearch"
              :success="Boolean(isClientFoundInDB)"
              :items="clients"
              :error="!inputFields.vat_id_buyer.validated"
              :label="inputFields.vat_id_buyer.label"
              return-object
              no-data-text
              item-text="Naziv"
              item-value="Davcna"
            />
          </VFlex>
          <VFlex
            v-if="!field['skip_on_doc_type'].includes(documentType)"
            v-for="(field, key) in inputFieldsToRender"
            :key="key"
            :class="`pr-3 pl-3 ${field.width}`"
          >
            <component
              :is="field.component"
              v-model="field.model"
              :success="field.model && field.validated"
              :items="field.items"
              :error="!field.validated"
              :label="field.label"
              :rules="field.rules"
              :disabled="field.disabled"
              @update="handleUpdate(key, $event)"
            />
          </VFlex>

          <template v-if="!isDocumentTypeOther">
            <VFlex v-if="additionalVats.length > 0" xs12>
              <VDivider class="pb-3" />
              <VLayout row wrap align-center>
                <VFlex xs11>
                  <VFlex v-for="vat in additionalVats" :key="vat.id" class="pa-1" xs12>
                    <VatCard
                      :data="vat"
                      :edit="true"
                      @onVatDelete="deleteAdditionalVat"
                      @onVatChange="updateAdditionalVat"
                      :vat-subject="partner?.vat_subject"
                    />
                  </VFlex>
                </VFlex>
                <VFlex v-if="additionalVats.length > 0 && partner?.vat_subject" xs1>
                  <VTooltip left>
                    <VBtn slot="activator" fab small dark color="info" @click="newVatCardBtn">
                      <VIcon v-if="!showNewVatCard"> add </VIcon>
                      <VIcon else> remove </VIcon>
                    </VBtn>
                    <span>Nova davčna stopnja</span>
                  </VTooltip>
                </VFlex>
              </VLayout>
              <VDivider class="mt-3" />
            </VFlex>
            <VFlex v-else xs12 style="display: flex" class="justify-end">
              <VTooltip left>
                <VBtn slot="activator" fab small dark color="info" @click="newVatCardBtn">
                  <VIcon v-if="!showNewVatCard"> add </VIcon>
                  <VIcon else> remove </VIcon>
                </VBtn>
                <span>Nova davčna stopnja</span>
              </VTooltip>
            </VFlex>
          </template>
          <VFlex pt-2 d-flex>
            <VFlex xs4 class="justify-start">
              <v-select
                v-model="documentType"
                :items="documentTypes"
                @change="changeDocumentType"
                return-object
                item-value="value"
                item-text="text"
                label="Tip dokumenta"
                hint="Izberite tip dokumenta"
                persistent-hint
                :disabled="isOriginalDocumentTypeIRN"
              />
            </VFlex>
            <VFlex xs6 class="justify-end"> </VFlex>
            <VTooltip class="action-btn" top>
              <VBtn
                slot="activator"
                icon
                fab
                type="submit"
                large
                flat
                color="success"
                :disabled="savingInvoice || invalid || documentType === null || !isClientFoundInDB"
              >
                <VIcon>done</VIcon>
              </VBtn>
              <span>Ob potrditvi bo račun shranjen v bazo</span>
            </VTooltip>
            <VTooltip class="action-btn" top>
              <VBtn
                slot="activator"
                icon
                fab
                large
                flat
                color="red"
                :disabled="savingInvoice"
                @click.native.stop="dialog = true"
              >
                <VIcon>clear</VIcon>
              </VBtn>
              <span>Račun in vsi podatki bodo zavrženi</span>
            </VTooltip>
            <VTooltip class="action-btn" top>
              <VBtn
                slot="activator"
                icon
                fab
                large
                flat
                color="orange"
                :disabled="savingInvoice"
                @click.native.stop="
                  dialogRejectionWithEmail = true;
                  rejection = reasonsForRejection[0];
                "
              >
                <VIcon>content_paste_off</VIcon>
              </VBtn>
              <span>Zavrnitev s pošiljanjem maila stranki</span>
            </VTooltip>
            <VTooltip top class="action-btn">
              <VBtn
                slot="activator"
                fab
                large
                flat
                icon
                color="info"
                :disabled="savingInvoice"
                @click="showMergeDocuments = !showMergeDocuments"
              >
                <VIcon>merge_type</VIcon>
              </VBtn>
              <span>Združite datoteke</span>
            </VTooltip>
          </VFlex>
          <VFlex xs6 sm6 md6 lg6>
            <VLayout v-if="mergedDocuments.length > 0" align-center>
              <VSelect
                v-model="mergedDocuments"
                :items="mergedDocuments"
                :menu-props="{ maxHeight: '400' }"
                item-text="file_name"
                item-value="url"
                return-object
                multiple
                chips
                deletable-chips
                label="Izbrani dokumenti za združitev"
                persistent-hint
                disabled
              />
            </VLayout>
          </VFlex>
        </VLayout>
      </VForm>
    </ValidationObserver>
    <VFadeTransition>
      <VFlex v-show="showNewVatCard && partner?.vat_subject" xs12 class="new_vat--card">
        <VatCard
          :additional-vats="additionalVats"
          :edit="false"
          @addNewVat="addNewVat"
          @closeVatCard="newVatCard = !newVatCard"
        />
      </VFlex>
    </VFadeTransition>
    <VLayout row justify-center>
      <VDialog v-model="dialog" persistent max-width="500">
        <VCard>
          <VCardTitle class="headline"> Želite zavreči račun? </VCardTitle>
          <VCardText>
            Vsi podatki in slika računa bodo izgubljeni. Potrebna bo ponovna obdelava
            <VTextarea v-model="rejection" label="Razlog zavrnitve*" type="string" />
          </VCardText>
          <VCardActions>
            <VSpacer />
            <VBtn
              color="teal darken-1"
              flat
              @click.native="
                dialog = false;
                rejection = '';
              "
            >
              Prekliči
            </VBtn>
            <VBtn
              color="teal darken-1"
              flat
              :disabled="rejection.length < 1"
              @click.native="clearInvoiceData(true, statuses.REJECTED)"
            >
              Zavrži
            </VBtn>
          </VCardActions>
        </VCard>
      </VDialog>
      <VDialog v-model="dialogRejectionWithEmail" persistent max-width="500">
        <VCard>
          <VCardTitle class="headline">
            Želite zavreči račun s pošiljanjem maila stranki?
          </VCardTitle>
          <VCardText>
            Izberite vzrok zavrnitve:
            <v-select v-model="rejection" :items="reasonsForRejection" />
          </VCardText>
          <VCardActions>
            <VSpacer />
            <VBtn
              color="teal darken-1"
              flat
              @click.native="
                dialogRejectionWithEmail = false;
                rejection = '';
              "
            >
              Prekliči
            </VBtn>
            <VBtn
              color="teal darken-1"
              flat
              :disabled="rejection.length < 1"
              @click.native="clearInvoiceData(true, statuses.REJECTED_FOR_USER)"
            >
              Zavrži
            </VBtn>
          </VCardActions>
        </VCard>
      </VDialog>
    </VLayout>
    <VDialog v-model="showMergeDocuments" persistent max-width="1500">
      <VCard style="max-height: 900px !important">
        <VCardText>
          <VLayout style="height: 65vh; max-height: 800px">
            <v-flex xs4 style>
              <p class="headline ma-3 mb-4">Računi za združevanje</p>
              <VList
                v-if="documentsForMerge.length > 0"
                class="pa-0"
                style="height: calc(100% - 102px); overflow-y: auto"
                three-line
              >
                <VListTile
                  v-for="invoice in documentsForMerge"
                  :key="invoice.url"
                  style="height: 115px !important"
                  @click="setMergedInvoice(invoice)"
                >
                  <VListTileContent
                    class="list-tile"
                    :class="{ active: mergeInvoice && mergeInvoice.url === invoice.url }"
                  >
                    <VListTileTitle v-text="invoice.file_name" />
                    <VListTileSubTitle
                      v-if="invoice.attachment"
                      style="display: block; white-space: initial"
                    >
                      <div>
                        {{ invoice.attachment.received_mail.from_mail }}
                      </div>
                      <div>
                        {{ invoice.attachment.received_mail.subject }}
                      </div>
                      <div>
                        {{ invoice.created_at | moment('DD.MM.YYYY, HH:mm') }}
                      </div>
                    </VListTileSubTitle>
                    <VListTileSubTitle v-else style="display: block; white-space: initial">
                      <div>Poslano iz aplikacije Unija Scan</div>
                    </VListTileSubTitle>
                  </VListTileContent>

                  <v-list-tile-action>
                    <v-checkbox
                      color="primary"
                      :input-value="
                        mergedDocuments.some((mergedInvoice) => mergedInvoice === invoice)
                      "
                      @click.stop="toggleMergedDocument(invoice)"
                    />
                  </v-list-tile-action>
                </VListTile>
              </VList>
              <div class="invoice-pagination">
                <VBtn
                  flat
                  icon
                  large
                  color="primary"
                  :disabled="!pagination.previous"
                  @click="getUnprocessedInvoices(pagination.previous)"
                >
                  <VIcon>navigate_before</VIcon>
                </VBtn>
                <VBtn
                  flat
                  color="primary"
                  @click="getUnprocessedInvoices('received-invoices/?status=WR&limit=8')"
                >
                  Osveži račune
                </VBtn>
                <VBtn
                  flat
                  icon
                  large
                  color="primary"
                  :disabled="!pagination.next"
                  @click="getUnprocessedInvoices(pagination.next)"
                >
                  <VIcon>navigate_next</VIcon>
                </VBtn>
              </div>
            </v-flex>
            <v-flex xs8>
              <Viewer
                v-if="mergeInvoice"
                id="merge-viewer"
                :file="mergeInvoice.file"
                :fileName="mergeInvoice.file_name"
                :mailFrom="mergeInvoice.attachment.received_mail.from_mail"
                :mailSubject="mergeInvoice.attachment.received_mail.subject"
                :mailDate="mergeInvoice.attachment.received_mail.sent_date"
              ></Viewer>
              <div v-else class="no-invoice-to-merge">
                <p>Izberite dokumente, ki jih želite združiti</p>
                <div>
                  <v-icon>description</v-icon>
                  <v-icon>add</v-icon>
                </div>
              </div>
            </v-flex>
          </VLayout>
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn color="teal darken-1" flat @click.native="clearMergedDocuments"> Prekliči </VBtn>
          <VBtn color="teal darken-1" flat @click.native="showMergeDocuments = false">
            Potrdi
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
    <VLayout row justify-center>
      <VDialog v-model="dialogVatIdNotOnInvoice" persistent max-width="500">
        <VCard>
          <VCardTitle class="headline">
            Davčna številka prejemnika ni bila najdena na računu. Želite vseeno potrditi?
          </VCardTitle>
          <VCardActions>
            <VSpacer />
            <VBtn color="teal darken-1" flat @click.native="dialogVatIdNotOnInvoice = false">
              Prekliči
            </VBtn>
            <VBtn color="teal darken-1" flat @click.native="uploadInvoiceToServer"> Potrdi </VBtn>
          </VCardActions>
        </VCard>
      </VDialog>
    </VLayout>
  </VLayout>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import { debounce, isString } from 'lodash';
import FormConfig from '../../public/form_config.json';
import validationMixin from '../mixins/validationMixin';
import calculateMixin from '../mixins/calculateMixin';
import mappingMixin from '../mixins/mappingMixin';
import VatCard from './VatCard.vue';
import DateInput from './inputs/DateInput.vue';
import NumberInput from './inputs/NumberInput.vue';
import TextInput from './inputs/TextInput.vue';
import DropdownInput from './inputs/DropdownInput.vue';
import CompanyInput from './inputs/CompanyInput.vue';
import { generateInvKey } from '../helper';
import { axiosInstance, getInvoiceData } from '../services/api';
import axios from 'axios';
import Constants from '../constants';
import Viewer from '@/components/preview/Viewer.vue';

export default {
  name: 'InvoiceTab',
  components: {
    Viewer,
    VatCard,
    DateInput,
    NumberInput,
    TextInput,
    DropdownInput,
    CompanyInput,
    ValidationObserver,
    ValidationProvider,
  },
  mixins: [validationMixin, calculateMixin, mappingMixin],
  data() {
    return {
      newVatCard: false,
      showMergeDocuments: false,
      mergedDocuments: [],
      documentType: 'INV',
      useInternalInvNum: false,
      isOriginalDocumentTypeIRN: false,
      reasonsForRejection: [
        'Slaba kvaliteta scana/slike',
        'Odrezan del dokumenta',
        'Ni pravilno skeniran dokument',
        'Dokument ne vsebuje vseh potrebnih podatkov',
      ],
      allDocumentTypes: [
        {
          text: 'Račun',
          value: 'INV',
        },
        {
          text: 'Ostalo',
          value: 'OST',
        },
        {
          text: 'Izdani račun',
          value: 'IRN',
        },
      ],
      documentTypes: [
        {
          text: 'Račun',
          value: 'INV',
        },
        {
          text: 'Ostalo',
          value: 'OST',
        },
        {
          text: 'Izdani račun',
          value: 'IRN',
        },
      ],
      firstLoad: true,
      savingInvoice: false,
      additionalVats: [],
      inputFields: Object.assign({}, FormConfig),
      dialog: false,
      dialogRejectionWithEmail: false,
      dialogVatIdNotOnInvoice: false,
      rejection: '',
      rejectionWithEmail: '',
      clientSearch: null,
      partnersSearch: null,
      partners: [],
      loadingPartners: false,
      clients: [],
      loadingClients: false,
      partner: null,
      client: null,
      isClientFoundInDB: false,
      isPartnerFoundInDB: false,
      debouncedPartnerSearch: null,
      debouncedClientSearch: null,
      mergeInvoice: null,
      currency: 'EUR',
      statuses: Constants,
      partnerSearchCancelToken: null,
      clientSearchCancelToken: null,
    };
  },
  props: {
    selectedInvoice: {
      default: null,
    },
  },
  computed: {
    ...mapGetters([
      'showNewVatCard',
      'unProcessedInvoices',
      'unreviewedCount',
      'pagination',
      'currencies',
      'countries',
      'paymentTypes',
    ]),
    currencyCodes() {
      return this.currencies.map((currency) => ({
        text: currency.iso_code,
        value: currency.iso_code,
      }));
    },
    paymentTypeNames() {
      return this.paymentTypes.map((type) => ({ text: type.name, value: type.mapping }));
    },
    inputFieldsToRender() {
      const inputFieldsToRender = this._.cloneDeep(this.inputFields);

      if (!this.useInternalInvNum) {
        delete inputFieldsToRender.internal_invoice_number;
      }

      if (!this.partner || !this.partner.payment_processing) {
        delete inputFieldsToRender.purpose_code;
        delete inputFieldsToRender.iban;
        delete inputFieldsToRender.prepaid_amount;
      }

      if (this.partner?.external_api === 'Pantheon' && this.partner?.country === 'HR') {
        inputFieldsToRender.payment_type.items = this.paymentTypeNames;
      } else {
        delete inputFieldsToRender.payment_type;
      }

      inputFieldsToRender.currency.items = this.currencyCodes;
      inputFieldsToRender.vat.rules = { vat: this.additionalVats };

      if (!this.partner?.vat_subject) {
        inputFieldsToRender.vat.rules = {};
        inputFieldsToRender.vat.disabled = true;
        inputFieldsToRender.vat.model = '0.00';
        this.$set(this.inputFields.vat, 'model', '0.00');
      }
      return inputFieldsToRender;
    },
    documentsForMerge() {
      return this.unProcessedInvoices.filter((doc) => doc.url !== this.selectedInvoice.url);
    },
    isDocumentTypeOther() {
      return this.documentType === 'OST' || this.documentType.value === 'OST';
    },
  },
  watch: {
    partnersSearch: {
      handler(value) {
        if (!value) {
          this.partners = [];
          return;
        }
        if (value.length <= 3) {
          return;
        }
        if (
          this.partner &&
          typeof this.partner === 'object' &&
          (this.partner.Davcna === value || this.partner.Naziv === value)
        ) {
          return;
        }

        this.debouncedPartnerSearch(value);
      },
    },
    clientSearch: {
      handler(value) {
        if (!value) {
          this.clients = [];
          return;
        }
        if (value.length <= 3 || !this.partner?.Davcna) {
          return;
        }
        if (
          this.client &&
          typeof this.client === 'object' &&
          (this.client.Davcna === value || this.client.Naziv === value)
        ) {
          return;
        }
        const oldPartnerIsLoaded =
          this.removeNoneDigits(this.partner.Davcna) !==
            this.removeNoneDigits(this.partnersSearch) &&
          this.partner.Naziv !== this.partnersSearch;

        if (oldPartnerIsLoaded) {
          return;
        }
        this.debouncedClientSearch(value);
      },
    },
    client: {
      handler(value) {
        // Verify if client was selected from the dropdown
        if (value && typeof value === 'object') {
          this.isClientFoundInDB = true;
        } else {
          this.isClientFoundInDB = false;
        }
      },
    },
    partner: {
      handler(value) {
        if (value && typeof value === 'object') {
          this.setupPartnerConfiguration();
          this.$emit('vat-id-buyer', this.partner.Davcna);
          this.isPartnerFoundInDB = true;
          this.isClientFoundInDB = false;
          this.client = this.clientSearch;
          this.clients = [];

          this.currency = this.getBuyerCurrency(value);
          this.inputFields['currency'].model = this.currency;
          this.updateBuyerAdditionalVats(value);

          // Only trigger client search after partner is properly set up
          if (this.clientSearch && value.url) {
            this.debouncedClientSearch(this.clientSearch);
          }
          return;
        }
        this.isPartnerFoundInDB = false;
        this.clients = [];
        this.isClientFoundInDB = false;
      },
      immediate: true,
    },
    'inputFields.inv_date.model': {
      handler(newValue) {
        this.inputFields.received_at.model = newValue;
      },
      deep: true,
    },
    selectedInvoice: {
      async handler(value) {
        try {
          this.$emit('loadingData', true);

          // 1. Cancel all pending operations
          if (this.partnerSearchCancelToken) {
            this.partnerSearchCancelToken.cancel('Document changed');
            this.partnerSearchCancelToken = null;
          }
          if (this.clientSearchCancelToken) {
            this.clientSearchCancelToken.cancel('Document changed');
            this.clientSearchCancelToken = null;
          }
          if (this.debouncedPartnerSearch?.cancel) {
            this.debouncedPartnerSearch.cancel();
          }
          if (this.debouncedClientSearch?.cancel) {
            this.debouncedClientSearch.cancel();
          }

          // 2. Clear state
          this.firstLoad = true;
          this.mergedDocuments = [];
          this.showMergeDocuments = false;
          this.additionalVats = [];
          this.partners = [];
          this.clients = [];
          this.partner = null;
          this.client = null;
          this.isClientFoundInDB = false;
          this.isPartnerFoundInDB = false;
          this.isOriginalDocumentTypeIRN = false;

          // 3. Load new data
          if (value) {
            await this.loadInvoiceDetails(value);

            // 4. Trigger partner search first
            if (this.inputFields.vat_id_buyer.model) {
              this.partnersSearch = this.inputFields.vat_id_buyer.model;
              // Store client search value but don't trigger it yet
              this.clientSearch = this.inputFields.vat_id_publisher.model;
            }
          }
        } catch (e) {
          this.$emit('loadingDataError', e);
        } finally {
          this.$emit('loadingData', false);
        }
      },
      immediate: true,
    },
    additionalVats(value) {
      const summedNets = this.sumAllNetValues(value, this.firstLoad);
      const summedVats = this.sumAllVatValues(value, this.firstLoad);
      if (summedVats !== false) {
        this.inputFields.vat.model = summedVats;
      }
      if (summedNets !== false) {
        this.inputFields.net.model = summedNets;
      }
    },
  },
  created() {
    this.debouncedPartnerSearch = debounce(this.searchPartners, 600);
    this.debouncedClientSearch = debounce(this.searchClients, 600);
    this.getCurrenciesAction({});
    this.getCountriesAction({});
    this.getPaymentTypesAction();
  },
  beforeDestroy() {
    this.debouncedPartnerSearch.cancel();
    this.debouncedClientSearch.cancel();
  },
  methods: {
    ...mapActions([
      'setSnackBarStateAction',
      'deleteInvoiceFromDatabase',
      'saveInvoiceToDatabase',
      'getNextInvoiceForReview',
      'getUnprocessedInvoices',
      'getCurrenciesAction',
      'getCountriesAction',
      'getPaymentTypesAction',
    ]),
    ...mapMutations(['setActiveInvoice']),
    getBuyerCurrency(buyer) {
      const country = this.countries.find((country) => country.iso_code === buyer.country);
      if (country) {
        return country.currency;
      }
      return 'EUR';
    },
    updateBuyerAdditionalVats(buyer) {
      // remove previously added vats from country repository
      this.additionalVats = this.additionalVats.filter((vat) => !vat.auto_added);

      if (!buyer.vat_subject) {
        // For non-VAT subjects, ensure there's only one entry with 0% VAT
        const existingZeroVat = this.additionalVats.find(
          (item) => parseFloat(item.vat.percentage) === 0
        );

        if (!existingZeroVat) {
          const netValue =
            this.additionalVats.length > 0
              ? this.additionalVats
                  .reduce((sum, vat) => sum + parseFloat(vat.net.value[0]), 0)
                  .toFixed(2)
              : '0.00';
          const grossValue = netValue; // For non-VAT subjects, gross = net

          const newVat = this.mapVatValuesToValue([
            {
              gross: grossValue,
              net: netValue,
              vat: '0.00',
              percentage: '0.00',
            },
          ]).pop();

          newVat.id = generateInvKey();
          newVat.auto_added = true;
          this.additionalVats = [newVat];
        } else {
          this.additionalVats = [existingZeroVat];
        }
        return;
      }

      const country = this.countries.find((country) => country.iso_code === buyer.country);
      if (!country) {
        return;
      }
      // Don't override detected vats
      const existingAdditionalVatPercentages = this.additionalVats.map(
        (item) => item.vat.percentage
      );
      const vatsNotInAdditionalVats = country.vat_levels.filter(
        (vat) => !existingAdditionalVatPercentages.includes(vat.percentage)
      );

      for (const vat of vatsNotInAdditionalVats) {
        let newVat = this.mapVatValuesToValue([
          {
            gross: '0.00',
            net: '0.00',
            vat: '0.00',
            percentage: vat.percentage,
          },
        ]).pop();
        newVat.id = generateInvKey();
        newVat.auto_added = true;
        this.additionalVats.push(newVat);
      }
      this.additionalVats = this.sortAdditionalVats(this.additionalVats);
    },
    mapInternalPartnerList(internalPartnerList) {
      return internalPartnerList.map((internalPartner) => ({
        ...internalPartner,
        Naziv: internalPartner.name,
        NazivDolgi: internalPartner.name,
        Davcna: internalPartner.vat_id,
      }));
    },
    removeNoneDigits(query) {
      if (!query) {
        return '';
      }
      let regex = query.match(/\d+/g);
      if (regex && regex.length && regex[0].length > 3) {
        return regex[0];
      }
      return query;
    },
    async searchPartners(query) {
      if (!query) return;
      this.loadingPartners = true;

      if (this.partnerSearchCancelToken) {
        this.partnerSearchCancelToken.cancel('Operation canceled due to new request.');
      }

      this.partnerSearchCancelToken = axios.CancelToken.source();

      try {
        const res = await axiosInstance.get(`/partner/?query=${this.removeNoneDigits(query)}`);
        this.partners = this.mapInternalPartnerList(res.data);

        if (this.partners.length === 1) {
          this.partner = this.partners[0];
        } else if (this.partners.length === 0) {
          // If no partners found, set the partner to the queried tax ID
          const cleanQuery = this.removeNoneDigits(query);
          this.partner = cleanQuery;
        }
      } catch (err) {
        console.error('Error fetching partners:', err);
        this.partners = [];
      } finally {
        this.loadingPartners = false;
      }
    },
    async setupPartnerConfiguration() {
      // Reset to default state first
      this.documentTypes = [...this.allDocumentTypes];
      this.useInternalInvNum = false;

      // If no partner or no allowed_document_types, return early
      if (!this.partner?.allowed_document_types) {
        // If original document type is IRN, ensure it's in the list
        if (this.isOriginalDocumentTypeIRN) {
          const irnType = this.allDocumentTypes.find((type) => type.value === 'IRN');
          if (irnType && !this.documentTypes.find((type) => type.value === 'IRN')) {
            this.documentTypes = [irnType];
          }
        }
        return;
      }
      // Set configuration only if we have valid partner data
      this.useInternalInvNum = !!this.partner.use_internal_inv_num;
      this.documentTypes = this.allDocumentTypes.filter((type) =>
        this.partner.allowed_document_types.includes(type.value)
      );

      // If original document type is IRN, ensure it's always in the list
      if (this.isOriginalDocumentTypeIRN) {
        const irnType = this.allDocumentTypes.find((type) => type.value === 'IRN');
        if (irnType && !this.documentTypes.find((type) => type.value === 'IRN')) {
          this.documentTypes.push(irnType);
        }
      }
    },
    async searchClients(query) {
      if (!query) return;
      this.loadingClients = true;

      if (this.clientSearchCancelToken) {
        this.clientSearchCancelToken.cancel('Operation canceled due to new request.');
      }

      this.clientSearchCancelToken = axios.CancelToken.source();

      try {
        const res = await axiosInstance.get(
          `${this.partner.url}partners/?search=${this.removeNoneDigits(query)}`
        );
        this.clients = res.data;

        if (this.clients.length === 1) {
          this.client = this.clients[0];
        }
      } catch (err) {
        console.error('Error fetching clients:', err);
        this.clients = [];
      } finally {
        this.loadingClients = false;
      }
    },
    handleUpdate(key, payload) {
      this.inputFields[key].model = payload;
      if (key === 'currency') {
        this.currency = payload;
      }
    },
    loadInvoiceDetails(invoice) {
      if (!invoice) {
        return;
      }
      const typlessVersion =
        invoice.typless_v2_id && invoice.details_v2 && invoice.details_v2.inv_num ? 2 : 1;
      const typlessDetails = typlessVersion === 2 ? invoice.details_v2 : invoice.details;
      this.inputFields = this.mapDataToInputFields(typlessDetails, typlessVersion);
      const vats = typlessDetails.vat_rates || typlessDetails.vats;

      if (vats && vats.length > 0) {
        if (isString(vats[0].vat)) {
          const mappedVats = this.mapVatValuesToValue(vats, typlessVersion);
          this.additionalVats = mappedVats.map((object) => ({
            ...object,
            id: generateInvKey(),
          }));
        } else {
          this.additionalVats = vats.map((object) => ({
            ...object,
            id: generateInvKey(),
          }));
        }
        this.additionalVats = this.sortAdditionalVats(this.additionalVats);
      }

      this.documentType = invoice.document_type;
      this.isOriginalDocumentTypeIRN = invoice.document_type === 'IRN';
      this.partnersSearch = this.inputFields.vat_id_buyer.model;
      this.clientSearch = this.inputFields.vat_id_publisher.model;

      this.$nextTick(() => {
        this.firstLoad = false;
      });
    },
    newVatCardBtn() {
      this.$store.commit('setShowNewVatCard', !this.showNewVatCard);
    },
    async uploadInvoiceToServer() {
      const valid = await this.$refs.observer.validate();
      if (valid || this.isDocumentTypeOther) {
        await this.updateInvoiceData();
      } else {
        this.dialogVatIdNotOnInvoice = false;
        this.savingInvoice = false;
      }
    },
    async setMergedInvoice(invoice) {
      try {
        const { data } = await getInvoiceData(invoice.url);
        this.mergeInvoice = data;
      } catch (e) {
        console.error(e);
        this.setSnackBarStateAction({
          snackBar: true,
          color: 'red',
          text: 'Napaka pri pridobivanju podatkov, kontaktirajte administratorja',
        });
      }
    },
    toggleMergedDocument(invoice) {
      const index = this.mergedDocuments.indexOf(invoice);
      if (index !== -1) {
        this.mergedDocuments.splice(index, 1);
      } else {
        this.mergedDocuments.push(invoice);
      }
    },
    clearMergedDocuments() {
      this.mergeInvoice = null;
      this.showMergeDocuments = false;
      this.mergedDocuments = [];
    },
    changeDocumentType(newType) {
      this.documentType = newType.value;
    },
    async updateInvoice() {
      // Immediately disable the button to prevent double-clicks
      if (this.savingInvoice) return;
      this.savingInvoice = true;

      // Check if current document type is allowed (skip check if original was IRN)
      if (!this.isOriginalDocumentTypeIRN) {
        const found = this.documentTypes.filter((doctype) => doctype.value === this.documentType);
        if (!found.length) {
          this.setSnackBarStateAction({
            snackBar: true,
            color: 'info',
            text: 'Trenutni tip dokumenta se ne ujema z dovoljenimi tipi za prejemnika.',
          });
          this.savingInvoice = false;
          return;
        }
      }

      try {
        const { data } = await axiosInstance.get(
          `${this.selectedInvoice.url}check-vat-id-buyer/?vat_id_buyer=${this.partner.Davcna}`
        );
        if (!data.is_present) {
          this.dialogVatIdNotOnInvoice = true;
          this.savingInvoice = false;
          return;
        }
        await this.uploadInvoiceToServer();
      } catch (e) {
        console.error('Error in updateInvoice:', e);
        this.savingInvoice = false;
      }
    },
    async updateInvoiceData() {
      let invoiceData;
      if (!this.isPartnerFoundInDB) {
        this.setSnackBarStateAction({
          snackBar: true,
          color: 'info',
          text: 'Kupec ni bil najden v šifrantu. Preverite šifrant ali izberite drugo podjetje.',
        });
        return;
      }
      if (this.isDocumentTypeOther) {
        invoiceData = {
          ...this.mapDefaultValuesToInvoiceObject(),
          vat_id_buyer: this.partner.Davcna || this.partner,
          vat_id_publisher: this.client.Davcna || this.client,
          inv_num: this.inputFields.inv_num.model,
        };
      } else {
        const vatObject = this.generateVatObjectArray(this.additionalVats);
        if (!this.inputFields.inv_num.model) {
          this.setSnackBarStateAction({
            snackBar: true,
            color: 'info',
            text: 'Številka računa je obvezen podatek',
          });
          return;
        }
        if (!this.inputFields.inv_num.model) {
          this.inputFields.inv_num.model = '999';
        }
        invoiceData = {
          ...this.mapPropertiesToInvoiceObject(
            this.inputFields,
            vatObject,
            this.partner.payment_processing
          ),
          vat_id_buyer: this.partner.Davcna || this.partner,
          vat_id_publisher: this.client.Davcna || this.client,
          seller_name: this.client.Naziv,
        };

        const [invDay, invMonth, invYear] = invoiceData.inv_date.split('.');
        const [receivedDay, receivedMonth, receivedYear] = invoiceData.received_at.split('.');
        const receivedAtIsBeforeInvDate =
          new Date(invYear, invMonth - 1, invDay) >
          new Date(receivedYear, receivedMonth - 1, receivedDay);
        if (receivedAtIsBeforeInvDate) {
          this.setSnackBarStateAction({
            snackBar: true,
            color: 'info',
            text: 'Datum prejema ne more biti pred datumom izdaje.',
          });
          this.dialogVatIdNotOnInvoice = false;
          return;
        }
      }
      let mergedDocumentKeys = this.mergedDocuments;
      if (
        mergedDocumentKeys.length > 0 &&
        !(mergedDocumentKeys.length === 1 && mergedDocumentKeys[0].url === this.selectedInvoice.url)
      ) {
        mergedDocumentKeys = [...new Set([this.selectedInvoice, ...mergedDocumentKeys])];
      }
      try {
        this.savingInvoice = true;
        this.$emit('loading', true);
        await this.saveInvoiceToDatabase({
          selectedInvoice: this.selectedInvoice,
          data: invoiceData,
          mergedDocumentKeys,
          documentType: this.documentType.value || this.documentType,
          partner: this.partner && typeof this.partner === 'object' ? this.partner.id : null,
        });
        this.savingInvoice = false;
        this.dialogVatIdNotOnInvoice = false;
        this.$emit('loading', false);
        await this.clearInvoiceData(false);
        this.setSnackBarStateAction({
          snackBar: true,
          color: 'success',
          text: 'Podatki iz računa so bili uspešno shranjeni v bazo',
        });
      } catch (e) {
        if (e.response.status === 400) {
          this.setSnackBarStateAction({
            snackBar: true,
            color: 'red',
            text: e.response.data.details,
          });
        } else {
          this.setSnackBarStateAction({
            snackBar: true,
            color: 'red',
            text: 'Napaka pri shranjevanju podatkov, kontaktirajte administratorja',
          });
        }
      } finally {
        this.documentType = 'INV';
        this.savingInvoice = false;
        this.$emit('loading', false);
      }
    },
    async clearInvoiceData(deleteData, status) {
      this.$store.commit('setActiveInvoice', null);
      if (deleteData) {
        this.dialogRejectionWithEmail = false;
        this.setSnackBarStateAction({
          snackBar: true,
          color: 'red',
          text: 'Račun je bil zavržen',
        });
        try {
          await this.deleteInvoiceFromDatabase({
            invoice: this.selectedInvoice,
            rejection: this.rejection,
            status,
          });
          this.dialog = false;
          this.rejection = '';
        } catch (e) {
          console.error(e);
        }
      }
      await this.getNextInvoiceForReview();
      this.getUnprocessedInvoices('received-invoices/?status=WR&limit=8');
    },
    addNewVat(vat) {
      this.additionalVats.push(vat);
      this.additionalVats = this.sortAdditionalVats(this.additionalVats);
    },
    deleteAdditionalVat(id) {
      this.additionalVats = this.sortAdditionalVats(
        this.additionalVats.filter((vat) => vat.id !== id)
      );
    },
    updateAdditionalVat(vatObject) {
      const index = this.additionalVats.findIndex((vat) => vat.id === vatObject.id);
      // Remove the object at the found index
      this.additionalVats.splice(index, 1, vatObject);
      this.additionalVats = this.sortAdditionalVats(this.additionalVats);
    },
  },
};
</script>

<style scoped>
.tab--container {
  flex-grow: 1;
  overflow: auto;
  display: flex;
  background-color: white;
}
.drawer_content_container {
  height: 100%;
  flex: auto;
  display: flex;
  flex-direction: column;
  overflow-y: scroll;
}

.new_vat--card {
  position: absolute;
  bottom: 0;
  background-color: white;
  left: 0;
  border-top: solid 1px #eee;
  width: 100%;
}

.action-btn {
  flex: 0 !important;
}
.invoice-pagination {
  height: 50px;
  width: 100%;
  border: solid 1px #eeeeee;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.scrollable-content {
  background-color: #ffa20057 !important;
  border-radius: 8px;
  max-height: 110px;
  overflow-y: scroll;
}

.no-invoice-to-merge {
  text-align: center;
  padding-top: 20px;
}

.no-invoice-to-merge p {
  font-size: xx-large;
  color: #303030;
}

.no-invoice-to-merge div {
  margin-top: 20%;
  margin-left: 10%;
}

.no-invoice-to-merge div i {
  font-size: 400px;
  margin: -50px;
  position: relative;
  opacity: 0.15;
}

.no-invoice-to-merge div i:nth-child(2) {
  top: -250px;
  font-size: 200px;
}

.list-tile {
  height: 115px;
  padding: 5px;
  margin-right: 30px;
  border-radius: 5px;
  transition: background-color 0.2s ease;
}
::v-deep .theme--light.v-list .v-list__group__header:hover,
::v-deep .theme--light.v-list .v-list__tile--highlighted,
::v-deep .theme--light.v-list .v-list__tile--link:hover {
  background: transparent !important;
}
.list-tile:hover {
  background-color: rgba(0, 0, 0, 0.04);
  transition: background-color 0.2s ease;
}
.active {
  background: rgba(0, 0, 0, 0.1);
}
.active:hover {
  background: rgba(0, 0, 0, 0.1);
}
</style>
